import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

const flagIcon = L.divIcon({
  className: "fairway-flag-marker",
  html: `
    <div style="
      transform: translate(-50%, -100%);
      display: flex; flex-direction: column; align-items: center;
    ">
      <div style="
        background: #143D2F;
        color: white;
        width: 28px; height: 28px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 4px 10px rgba(0,0,0,0.25);
        border: 2px solid white;
      ">
        <span style="transform: rotate(45deg); font-size: 12px;">⛳</span>
      </div>
    </div>
  `,
  iconSize: [28, 36],
  iconAnchor: [14, 36],
});

const addPinIcon = L.divIcon({
  className: "fairway-add-marker",
  html: `
    <div style="
      width: 30px; height: 30px;
      border-radius: 50% 50% 50% 0;
      transform: translate(-50%, -100%) rotate(-45deg);
      background: #C4543C;
      border: 2px solid white;
      box-shadow: 0 4px 10px rgba(0,0,0,0.25);
      display: flex; align-items: center; justify-content: center;
    ">
      <span style="transform: rotate(45deg); color: white; font-weight: 700; font-size: 16px;">+</span>
    </div>
  `,
  iconSize: [30, 38],
  iconAnchor: [15, 38],
});

const youAreHereIcon = L.divIcon({
  className: "fairway-you-here",
  html: `
    <div style="
      width: 22px; height: 22px;
      border-radius: 50%;
      background: #2563EB;
      border: 4px solid white;
      box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.35), 0 4px 10px rgba(0,0,0,0.25);
      transform: translate(-50%, -50%);
    "></div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dp = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c) / 1609.344;
}

// Custom cluster icon styled to match our palette
const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 40 : count < 100 ? 48 : count < 500 ? 56 : 64;
  return L.divIcon({
    html: `
      <div style="
        width: ${size}px; height: ${size}px;
        background: rgba(20, 61, 47, 0.92);
        color: white;
        border: 3px solid white;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-family: 'Outfit', sans-serif;
        font-weight: 800;
        font-size: ${count < 100 ? 14 : 13}px;
        box-shadow: 0 6px 14px rgba(20,61,47,0.35);
      ">${count}</div>
    `,
    className: "fairway-cluster",
    iconSize: [size, size],
  });
};

function courseCentroid(course) {
  // Lite course payloads include a precomputed centroid so we don't need to
  // ship every hole coordinate to the client. Fall back to computing from
  // holes for old/full-shape payloads.
  if (course.centroid_lat != null && course.centroid_lng != null) {
    return { lat: course.centroid_lat, lng: course.centroid_lng };
  }
  const coords = (course.holes || []).filter((h) => h.lat != null && h.lng != null);
  if (!coords.length) return null;
  const lat = coords.reduce((s, h) => s + h.lat, 0) / coords.length;
  const lng = coords.reduce((s, h) => s + h.lng, 0) / coords.length;
  return { lat, lng };
}

function ClickToAdd({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng); },
  });
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target || !map) return;
    const id = setTimeout(() => {
      map.invalidateSize();
      map.flyTo([target.lat, target.lng], target.zoom ?? 14, { duration: 1.4 });
    }, 250);
    return () => clearTimeout(id);
  }, [target, map]);
  return null;
}

function ClusterLayer({ courses, onViewCourse }) {
  const map = useMap();
  const clusterRef = useRef(null);

  // Mount the cluster group exactly once. Re-creating it on every `courses`
  // change races Leaflet's internal layer teardown and crashes with
  // "Cannot read properties of null (reading 'getMinZoom')" — so we keep
  // a single cluster instance and just sync markers below.
  useEffect(() => {
    const cluster = L.markerClusterGroup({
      // No chunkedLoading — it defers addLayer calls and races strict-mode
      // remount, crashing with "Cannot read properties of null (reading
      // 'getMinZoom')" when the unmount tears down the map first.
      maxClusterRadius: 55,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: createClusterIcon,
    });
    map.addLayer(cluster);
    clusterRef.current = cluster;
    return () => {
      try { map.removeLayer(cluster); } catch { /* map already gone */ }
      clusterRef.current = null;
    };
  }, [map]);

  // Sync the marker set whenever `courses` changes — clear + re-add. This is
  // O(N) but markercluster's chunked loading keeps the UI responsive even
  // for the full 3.5k-course list.
  useEffect(() => {
    const cluster = clusterRef.current;
    if (!cluster) return;
    cluster.clearLayers();
    const markers = [];
    courses.forEach((c) => {
      const ctr = courseCentroid(c);
      if (!ctr) return;
      const m = L.marker([ctr.lat, ctr.lng], { icon: flagIcon });
      const popupHtml = `
        <div style="min-width:180px;font-family:'DM Sans',sans-serif;">
          <div style="font-family:'Outfit',sans-serif;font-weight:800;color:#143D2F;font-size:14px;line-height:1.2;">${escapeHtml(c.name)}</div>
          <div style="font-size:12px;color:#5C5C5C;margin-top:2px;">${escapeHtml(c.location || "")}</div>
          <div style="display:flex;gap:8px;font-size:11px;margin-top:6px;color:#143D2F;">
            <span><strong>Par</strong> ${c.par_total ?? "—"}</span>
            <span><strong>Yds</strong> ${c.yards_total ?? "—"}</span>
          </div>
          <button data-course-id="${c.id}" class="fairway-view-course-btn" style="
            margin-top:8px;width:100%;background:#143D2F;color:white;padding:6px 12px;
            border-radius:9999px;font-weight:600;border:0;cursor:pointer;
            font-family:'Outfit',sans-serif;font-size:12px;
          ">View course →</button>
        </div>
      `;
      m.bindPopup(popupHtml);
      m.on("popupopen", () => {
        setTimeout(() => {
          const btn = document.querySelector(`button.fairway-view-course-btn[data-course-id="${c.id}"]`);
          if (btn) btn.onclick = () => onViewCourse(c.id);
        }, 0);
      });
      markers.push(m);
    });
    cluster.addLayers(markers);
  }, [courses, onViewCourse]);

  return null;
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

export default function CoursesMap({ courses, onPickToAdd, focusCoords }) {
  const nav = useNavigate();
  const [pendingPin, setPendingPin] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [showClosest, setShowClosest] = useState(false);

  const handleMapClick = (latlng) => setPendingPin(latlng);
  const confirmAdd = () => {
    if (!pendingPin) return;
    const { lat, lng } = pendingPin;
    setPendingPin(null);
    onPickToAdd({ lat, lng });
  };

  const locateMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setShowClosest(true);
        setLocating(false);
      },
      () => { setLocating(false); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const closest10 = userPos
    ? courses
        .map((c) => {
          const ctr = courseCentroid(c);
          if (!ctr) return null;
          return { course: c, miles: haversineMiles(userPos.lat, userPos.lng, ctr.lat, ctr.lng) };
        })
        .filter(Boolean)
        .sort((a, b) => a.miles - b.miles)
        .slice(0, 10)
    : [];

  // When userPos becomes available, focus the map there (zoom 11 = ~10 mile radius visible)
  const userFocus = userPos ? { lat: userPos.lat, lng: userPos.lng, zoom: 11, _at: Date.now() } : null;
  const effectiveFocus = focusCoords || userFocus;

  return (
    <div className="relative rounded-3xl overflow-hidden border border-[var(--border-default)]" style={{ height: "65vh" }} data-testid="courses-map">
      <MapContainer
        center={effectiveFocus ? [effectiveFocus.lat, effectiveFocus.lng] : [54.5, -3.5]}
        zoom={effectiveFocus ? (effectiveFocus.zoom ?? 14) : 6}
        scrollWheelZoom={true}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClusterLayer courses={courses} onViewCourse={(id) => nav(`/app/courses/${id}`)} />
        <ClickToAdd onPick={handleMapClick} />
        <FlyTo target={effectiveFocus} />
        {userPos && (
          <Marker position={[userPos.lat, userPos.lng]} icon={youAreHereIcon}>
            <Popup>You are here</Popup>
          </Marker>
        )}
        {pendingPin && (
          <Marker position={[pendingPin.lat, pendingPin.lng]} icon={addPinIcon}>
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div style={{ fontFamily: "Outfit, sans-serif", fontWeight: 800, color: "#C4543C", fontSize: 14 }}>
                  Add a course here?
                </div>
                <div style={{ fontSize: 11, color: "#5C5C5C", marginTop: 2 }}>
                  Lat <span style={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 700 }}>{pendingPin.lat.toFixed(5)}</span>,
                  &nbsp;Lng <span style={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 700 }}>{pendingPin.lng.toFixed(5)}</span>
                </div>
                <button
                  onClick={confirmAdd}
                  data-testid="map-confirm-add-here"
                  style={{
                    marginTop: 8, width: "100%", background: "#C4543C", color: "white",
                    padding: "8px 12px", borderRadius: 9999, fontWeight: 600, border: 0,
                    cursor: "pointer", fontFamily: "Outfit, sans-serif", fontSize: 12,
                  }}
                >
                  Add course at this location
                </button>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      <div className="absolute top-3 left-3 z-[400] bg-white/95 backdrop-blur rounded-full px-3 py-1 text-xs font-heading font-semibold text-[var(--primary)] shadow-md pointer-events-none">
        Tap cluster to zoom · Tap empty area to add
      </div>

      {/* Near-me FAB */}
      <button
        onClick={locateMe}
        disabled={locating}
        data-testid="near-me-button"
        className="absolute top-3 right-3 z-[400] bg-white rounded-full shadow-md px-4 py-2 text-xs font-heading font-bold text-[var(--primary)] flex items-center gap-1.5 hover:bg-[var(--secondary)] transition-colors"
      >
        <span className="relative flex h-2 w-2">
          <span className={`absolute inset-0 rounded-full bg-blue-500 ${locating ? "pulse-dot" : ""}`} />
        </span>
        {locating ? "Locating…" : userPos ? "Refresh" : "Near me"}
      </button>

      {/* Closest 10 list */}
      {showClosest && closest10.length > 0 && (
        <div className="absolute bottom-3 left-3 right-3 z-[400] bg-white rounded-2xl shadow-lg max-h-[35%] overflow-y-auto border border-[var(--border-default)]" data-testid="closest-courses-panel">
          <div className="sticky top-0 bg-white px-4 py-2 border-b border-[var(--border-default)] flex items-center justify-between">
            <div>
              <p className="label-uppercase">Closest courses</p>
              <p className="font-heading font-bold text-sm text-[var(--primary)]">Within reach of you</p>
            </div>
            <button onClick={() => setShowClosest(false)} className="text-xs text-[var(--text-secondary)] underline" data-testid="closest-close">Hide</button>
          </div>
          <ul>
            {closest10.map(({ course, miles }, i) => (
              <li key={course.id} className="border-b border-[var(--border-default)] last:border-b-0">
                <button
                  onClick={() => nav(`/app/courses/${course.id}`)}
                  data-testid={`closest-row-${i}`}
                  className="w-full text-left px-4 py-2.5 hover:bg-[var(--bg-app)] flex items-center justify-between"
                >
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="font-heading font-semibold text-sm text-[var(--primary)] line-clamp-1">{course.name}</div>
                    <div className="text-[11px] text-[var(--text-secondary)] line-clamp-1">{course.location}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-num font-bold text-[var(--primary)]">{miles.toFixed(1)} mi</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
